import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config';
import AdminLayout from './AdminLayout';
import './Admin.css';

function ActivityLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterAction, setFilterAction] = useState('all');
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchLogs();
    }, [pagination.page, filterAction]);

    const fetchLogs = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!currentUser) {
                throw new Error('Not authenticated');
            }

            const token = await currentUser.getIdToken();
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...(filterAction !== 'all' && { action: filterAction })
            });

            const response = await fetch(`${API_URL}/api/admin/logs?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setLogs(data.logs || []);
                setPagination(prev => ({ ...prev, total: data.total || 0 }));
            } else {
                setLogs([]);
            }
        } catch (err) {
            console.error('Logs fetch error:', err);
            setError(err.message);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp._seconds
            ? new Date(timestamp._seconds * 1000)
            : new Date(timestamp);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getActionIcon = (action) => {
        const icons = {
            'user_banned': '🚫',
            'user_unbanned': '✅',
            'content_approved': '👍',
            'content_rejected': '👎',
            'content_flagged': '🚩',
            'topic_created': '🏷️',
            'topic_updated': '✏️',
            'settings_changed': '⚙️',
            'login': '🔐',
            'logout': '🚪'
        };
        return icons[action] || '📋';
    };

    const getActionBadge = (action) => {
        const types = {
            'user_banned': 'danger',
            'user_unbanned': 'success',
            'content_approved': 'success',
            'content_rejected': 'danger',
            'content_flagged': 'warning',
            'topic_created': 'info',
            'topic_updated': 'info',
            'settings_changed': 'secondary',
            'login': 'secondary',
            'logout': 'secondary'
        };
        const type = types[action] || 'secondary';
        return <span className={`badge badge-${type}`}>{action.replace(/_/g, ' ')}</span>;
    };

    const actionTypes = [
        'all',
        'user_banned',
        'user_unbanned',
        'content_approved',
        'content_rejected',
        'content_flagged',
        'topic_created',
        'topic_updated',
        'settings_changed'
    ];

    return (
        <AdminLayout>
            <div className="admin-logs">
                <div className="admin-header">
                    <h1>Activity Logs</h1>
                    <button className="refresh-btn" onClick={fetchLogs}>
                        🔄 Refresh
                    </button>
                </div>

                {/* Filters */}
                <div className="filters-bar">
                    <div className="filter-tabs scrollable">
                        {actionTypes.map(action => (
                            <button
                                key={action}
                                className={`filter-tab ${filterAction === action ? 'active' : ''}`}
                                onClick={() => {
                                    setFilterAction(action);
                                    setPagination(p => ({ ...p, page: 1 }));
                                }}
                            >
                                {action === 'all' ? 'All' : action.replace(/_/g, ' ')}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Logs List */}
                {loading ? (
                    <div className="admin-loading-container">
                        <div className="spinner"></div>
                        <p>Loading logs...</p>
                    </div>
                ) : error ? (
                    <div className="admin-error">
                        <p>{error}</p>
                        <button onClick={fetchLogs}>Retry</button>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="empty-state">
                        <span className="empty-icon">📋</span>
                        <h3>No activity logs found</h3>
                        <p>Admin actions will appear here.</p>
                    </div>
                ) : (
                    <>
                        <div className="logs-timeline">
                            {logs.map((log, idx) => (
                                <div key={log.id || idx} className="log-entry">
                                    <div className="log-icon">
                                        {getActionIcon(log.action)}
                                    </div>
                                    <div className="log-content">
                                        <div className="log-header">
                                            {getActionBadge(log.action)}
                                            <span className="log-time">{formatDate(log.timestamp)}</span>
                                        </div>
                                        <div className="log-details">
                                            <span className="log-admin">
                                                by <strong>{log.adminEmail || log.adminId}</strong>
                                            </span>
                                            {log.targetId && (
                                                <span className="log-target">
                                                    → {log.targetType}: {log.targetId}
                                                </span>
                                            )}
                                        </div>
                                        {log.details && (
                                            <div className="log-extra">
                                                {typeof log.details === 'object'
                                                    ? JSON.stringify(log.details)
                                                    : log.details
                                                }
                                            </div>
                                        )}
                                        {log.ipAddress && (
                                            <span className="log-ip">IP: {log.ipAddress}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="pagination">
                            <button
                                disabled={pagination.page <= 1}
                                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                            >
                                Previous
                            </button>
                            <span>Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit) || 1}</span>
                            <button
                                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}
            </div>
        </AdminLayout>
    );
}

export default ActivityLogs;
